import type { DateTime } from 'luxon'
import { useMemo } from 'react'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { filterByMarketProgram, filterByType } from '@/features/bessDashboard/utils/chart'

export type BidLadderForecastData = {
  clearingRangeLow: number
  clearingRangeHigh: number
  avgClearingPrice: number
  p50Low: number
  p50High: number
  p80Low: number
  p80High: number
  meanPrice: number
  opportunityCostPrices?: {
    fcrDUp: number
    fcrDDown: number
    fcrN: number
  }
}

export type OpportunityCostData = {
  opportunityCostPrices?: {
    fcrDUp: number
    fcrDDown: number
    fcrN: number
  }
}

export function useBidLadderForecast(
  forecastsData: FormattedRow[] | undefined,
  selectedMarket: string,
  selectedDeliveryDay: DateTime,
): BidLadderForecastData | null {
  return useMemo(() => {
    // Return null if no data available
    if (!forecastsData || forecastsData.length === 0) {
      return null
    }

    // Filter by market program
    const marketData = filterByMarketProgram(forecastsData, selectedMarket)

    // Filter by price forecast type
    const priceData = filterByType(marketData, 'capacity_price_per_MW_h_HOURLY')

    if (priceData.length === 0) {
      return null
    }

    // Format the selected date to match the forecast data format
    // The ds field in forecast data is in ISO format like "2025-01-15T10:00:00.000Z"
    const targetDateStr = selectedDeliveryDay.toFormat('yyyy-MM-dd')

    // Find data points for the selected date
    // We may have multiple data points per day (hourly data), so we need to aggregate
    const dayData = priceData.filter((row) => {
      const rowDateStr = String(row.ds).substring(0, 10)
      return rowDateStr === targetDateStr
    })

    if (dayData.length === 0) {
      return null
    }

    // Calculate min and max from all data points for the day
    // Extract both P50 and P80 confidence intervals
    let minP80 = Number.POSITIVE_INFINITY
    let maxP80 = Number.NEGATIVE_INFINITY
    let minP50 = Number.POSITIVE_INFINITY
    let maxP50 = Number.NEGATIVE_INFINITY
    let sumMean = 0

    dayData.forEach((row) => {
      const lo80 = Number(row['forecast-lo-80'])
      const hi80 = Number(row['forecast-hi-80'])
      const lo50 = Number(row['forecast-lo-50'])
      const hi50 = Number(row['forecast-hi-50'])
      const mean = Number(row['mean'])

      // P80 bounds
      if (!Number.isNaN(lo80) && lo80 < minP80) {
        minP80 = lo80
      }
      if (!Number.isNaN(hi80) && hi80 > maxP80) {
        maxP80 = hi80
      }

      // P50 bounds
      if (!Number.isNaN(lo50) && lo50 < minP50) {
        minP50 = lo50
      }
      if (!Number.isNaN(hi50) && hi50 > maxP50) {
        maxP50 = hi50
      }

      if (!Number.isNaN(mean)) {
        sumMean += mean
      }
    })

    // If we couldn't extract valid data, return null
    if (minP80 === Number.POSITIVE_INFINITY || maxP80 === Number.NEGATIVE_INFINITY) {
      return null
    }

    const avgMean = sumMean / dayData.length

    return {
      clearingRangeLow: Math.round(minP80 * 10) / 10,
      clearingRangeHigh: Math.round(maxP80 * 10) / 10,
      avgClearingPrice: Math.round(avgMean * 10) / 10,
      p50Low: Math.round(minP50 * 10) / 10,
      p50High: Math.round(maxP50 * 10) / 10,
      p80Low: Math.round(minP80 * 10) / 10,
      p80High: Math.round(maxP80 * 10) / 10,
      meanPrice: Math.round(avgMean * 10) / 10,
    }
  }, [forecastsData, selectedMarket, selectedDeliveryDay])
}
